<template>
  <div class="spangle-actions">
    <button
      v-if="showEditSizeButton"
      :disabled="isButtonDisabled"
      type="button"
      class="spangle-actions__button spangle-actions__button--secondary"
      @click="changeMode('edit-size')"
    >
      Edit Size
    </button>
    <button
      v-if="spangleSizeEditing"
      :disabled="isButtonDisabled"
      type="button"
      class="spangle-actions__button spangle-actions__button--text"
      @click="changeMode('size-edited')"
    >
      Cancel
    </button>
    <button
      v-if="showSpangleItButton"
      :disabled="isSpangleItButtonDisabled"
      type="button"
      class="spangle-actions__button spangle-actions__button--primary"
      @click="spangleIt"
    >
      <UseSvg class="spangle-actions__spangle-icon" icon="spangle-wand" />
      {{ spangleItButtonLabel }}
    </button>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { BUSINESS_LINE } from "common/utils/app-registry/contants";
import UseSvg from "common/vue/components/UseSvg.vue";
import { useDtfStore } from "./store";
import { useSpanglesStore } from "./spangles-module";

export default {
  name: "DtfSpanglesActions",
  components: { UseSvg },
  props: {
    isArtworkInvalid: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      resizeCounter: -1
    };
  },
  computed: {
    ...mapState(useDtfStore, ["artworks", "removeBgLoading"]),
    ...mapState(useSpanglesStore, [
      "lastSpangledDimensions",
      "spanglesLoading",
      "spangleSizeEdited",
      "spangleSizeEditing",
      "originalArtworkUrl",
      "allAPIsCompleted",
      "invalidSpangleSelected"
    ]),
    firstArtwork() {
      return this.artworks[0];
    },
    isSpangleItButtonDisabled() {
      const { width, height } = this.lastSpangledDimensions;
      const { width: originalWidth, height: originalHeight } = this.firstArtwork;
      const isDimensionSame =
        parseFloat(width) === parseFloat(originalWidth) &&
        parseFloat(height) === parseFloat(originalHeight);
      return (
        !this.allAPIsCompleted ||
        this.isArtworkInvalid ||
        this.removeBgLoading ||
        (this.spangleSizeEditing && isDimensionSame)
      );
    },
    isButtonDisabled() {
      return this.spanglesLoading || !this.allAPIsCompleted;
    },
    showEditSizeButton() {
      return this.spangleSizeEdited && !this.invalidSpangleSelected;
    },
    showSpangleItButton() {
      return !this.spangleSizeEdited || this.spanglesLoading || this.invalidSpangleSelected;
    },
    spangleItButtonLabel() {
      return this.allAPIsCompleted && !this.spanglesLoading ? "Spangle It!" : "Loading...";
    }
  },
  methods: {
    ...mapActions(useSpanglesStore, ["getSpangleVariations"]),
    ...mapActions(useSpanglesStore, ["setSpangleMode"]),
    ...mapActions(useDtfStore, ["updateArtworkValues"]),
    changeMode(mode) {
      this.setSpangleMode(mode);
    },
    logResizeCounter() {
      this.resizeCounter = this.invalidSpangleSelected
        ? this.resizeCounter
        : (this.resizeCounter += 1);
      submitDtfEvent("spangle_button_clicked", {
        artwork_url: this.originalArtworkUrl,
        resize_counter: this.resizeCounter,
        business_line: BUSINESS_LINE.SPANGLE
      });
    },
    spangleIt() {
      const processTimeStamp = Date.now();

      this.logResizeCounter();
      // show original image being spangled
      if (this.firstArtwork.spangled) {
        this.updateArtworkValues({ index: 0, spangled: false, spangledImageUrl: null });
      }
      this.updateArtworkValues({
        index: 0,
        processTimeStamp,
        isLoading: true,
        progress: 100,
        currentProgress: 0
      });
      this.getSpangleVariations({ isRetry: this.invalidSpangleSelected, processTimeStamp });
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.spangle-actions {
  display: flex;
  gap: 10px;
  font-family: $font-family-inter;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 16px;

  &__spangle-icon {
    width: 20px;
    height: 20px;
    color: $white;
  }

  &__button {
    height: 48px;
    flex-grow: 1;
    font-family: inherit;
    font-weight: inherit;
    font-size: inherit;
    cursor: pointer;
    border-radius: 8px;
    padding: 7px 14px;
    border: 1px solid $borneo-pink-gradient-start;

    &:disabled {
      cursor: not-allowed;
    }

    &--secondary {
      color: $borneo-pink-gradient-start;
      background-color: $white;

      &:disabled {
        color: $crafterpay-gray-modern-400;
        border-color: $crafterpay-gray-modern-400;
      }
    }

    &--primary {
      color: $white;
      background-color: $borneo-pink-gradient-start;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;

      &:disabled {
        background-color: $crafterpay-gray-modern-400;
        border-color: $crafterpay-gray-modern-400;
      }
    }

    &--text {
      color: $gray-modern-700;
      background-color: $white;
      border-color: $white;
    }
  }
}
</style>
